import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: window.localStorage
  }
});

export interface Database {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string;
          username: string;
          full_name: string;
          birth_date: string;
          user_type: 'artist' | 'visitor' | 'moderator' | 'admin';
          is_premium: boolean;
          avatar_url: string | null;
          banner_url: string | null;
          bio: string | null;
          art_categories: string[] | null;
          location: string | null;
          website: string | null;
          created_at: string;
          updated_at: string;
          age_verified: boolean;
          age_verification_status: 'unverified' | 'pending' | 'verified' | 'rejected';
          expo_push_token: string | null;
        };
        Insert: {
          id: string;
          username: string;
          full_name: string;
          birth_date: string;
          user_type: 'artist' | 'visitor' | 'moderator' | 'admin';
          is_premium?: boolean;
          avatar_url?: string | null;
          banner_url?: string | null;
          bio?: string | null;
          art_categories?: string[] | null;
          location?: string | null;
          website?: string | null;
          age_verified?: boolean;
          age_verification_status?: 'unverified' | 'pending' | 'verified' | 'rejected';
          expo_push_token?: string | null;
        };
        Update: {
          username?: string;
          full_name?: string;
          birth_date?: string;
          user_type?: 'artist' | 'visitor' | 'moderator' | 'admin';
          is_premium?: boolean;
          avatar_url?: string | null;
          banner_url?: string | null;
          bio?: string | null;
          art_categories?: string[] | null;
          location?: string | null;
          website?: string | null;
          updated_at?: string;
          age_verified?: boolean;
          age_verification_status?: 'unverified' | 'pending' | 'verified' | 'rejected';
          expo_push_token?: string | null;
        };
      };
      posts: {
        Row: {
          id: string;
          author_id: string;
          title: string;
          description: string;
          category: string;
          tags: string[] | null;
          is_blurred: boolean;
          for_sale: boolean;
          price: number | null;
          views_count: number;
          likes_count: number;
          comments_count: number;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          author_id: string;
          title: string;
          description: string;
          category: string;
          tags?: string[] | null;
          is_blurred?: boolean;
          for_sale?: boolean;
          price?: number | null;
          is_premium?: boolean;
        };
        Update: {
          title?: string;
          description?: string;
          category?: string;
          tags?: string[] | null;
          is_blurred?: boolean;
          for_sale?: boolean;
          price?: number | null;
          views_count?: number;
          updated_at?: string;
          is_premium?: boolean;
        };
      };
      media_files: {
        Row: {
          id: string;
          post_id: string;
          type: 'image' | 'video' | 'audio' | 'document' | '3d';
          url: string;
          file_name: string;
          file_size: number;
          duration: number | null;
          width: number | null;
          height: number | null;
          created_at: string;
        };
        Insert: {
          post_id: string;
          type: 'image' | 'video' | 'audio' | 'document' | '3d';
          url: string;
          file_name: string;
          file_size: number;
          duration?: number | null;
          width?: number | null;
          height?: number | null;
        };
      };
      comments: {
        Row: {
          id: string;
          post_id: string;
          author_id: string;
          content: string;
          likes_count: number;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          post_id: string;
          author_id: string;
          content: string;
        };
        Update: {
          content?: string;
          updated_at?: string;
        };
      };
      likes: {
        Row: {
          id: string;
          user_id: string;
          post_id: string | null;
          comment_id: string | null;
          created_at: string;
        };
        Insert: {
          user_id: string;
          post_id?: string | null;
          comment_id?: string | null;
        };
      };
      collections: {
        Row: {
          id: string;
          user_id: string;
          name: string;
          description: string | null;
          is_public: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          user_id: string;
          name: string;
          description?: string | null;
          is_public?: boolean;
        };
        Update: {
          name?: string;
          description?: string | null;
          is_public?: boolean;
          updated_at?: string;
        };
      };
      collaborations: {
        Row: {
          id: string;
          author_id: string;
          title: string;
          description: string;
          category: string;
          location: string | null;
          budget: number | null;
          deadline: string | null;
          requirements: string[] | null;
          status: 'open' | 'in_progress' | 'completed' | 'cancelled';
          created_at: string;
          updated_at: string;
        };
        Insert: {
          author_id: string;
          title: string;
          description: string;
          category: string;
          location?: string | null;
          budget?: number | null;
          deadline?: string | null;
          requirements?: string[] | null;
          status?: 'open' | 'in_progress' | 'completed' | 'cancelled';
        };
        Update: {
          title?: string;
          description?: string;
          category?: string;
          location?: string | null;
          budget?: number | null;
          deadline?: string | null;
          requirements?: string[] | null;
          status?: 'open' | 'in_progress' | 'completed' | 'cancelled';
          updated_at?: string;
        };
      };
      age_verifications: {
        Row: {
          id: string;
          user_id: string;
          status: 'pending' | 'verified' | 'rejected';
          document_type: string;
          document_country: string;
          document_number_last4: string | null;
          date_of_birth: string;
          provider: string | null;
          provider_reference: string | null;
          fraud_score: number | null;
          metadata: Record<string, unknown>;
          document_storage_path: string | null;
          created_at: string;
          updated_at: string;
          verified_at: string | null;
          rejected_reason: string | null;
        };
        Insert: {
          user_id: string;
          status?: 'pending' | 'verified' | 'rejected';
          document_type: string;
          document_country: string;
          document_number_last4?: string | null;
          date_of_birth: string;
          provider?: string | null;
          provider_reference?: string | null;
          fraud_score?: number | null;
          metadata?: Record<string, unknown>;
          document_storage_path?: string | null;
        };
        Update: {
          status?: 'pending' | 'verified' | 'rejected';
          provider?: string | null;
          provider_reference?: string | null;
          fraud_score?: number | null;
          metadata?: Record<string, unknown>;
          document_storage_path?: string | null;
          updated_at?: string;
          verified_at?: string | null;
          rejected_reason?: string | null;
        };
      };
    };
    notifications: {
      Row: {
        id: string;
        user_id: string;
        type: 'like' | 'comment' | 'follow' | 'collaboration' | 'sale' | 'system' | 'event';
        title: string;
        message: string;
        is_read: boolean;
        created_at: string;
        updated_at: string;
        related_user_id: string | null;
        related_post_id: string | null;
        related_comment_id: string | null;
        related_collaboration_id: string | null;
        related_event_id: string | null;
        metadata: Record<string, unknown>;
      };
      Insert: {
        user_id: string;
        type: 'like' | 'comment' | 'follow' | 'collaboration' | 'sale' | 'system' | 'event';
        title: string;
        message: string;
        is_read?: boolean;
        related_user_id?: string | null;
        related_post_id?: string | null;
        related_comment_id?: string | null;
        related_collaboration_id?: string | null;
        related_event_id?: string | null;
        metadata?: Record<string, unknown>;
      };
      Update: {
        type?: 'like' | 'comment' | 'follow' | 'collaboration' | 'sale' | 'system' | 'event';
        title?: string;
        message?: string;
        is_read?: boolean;
        updated_at?: string;
        related_user_id?: string | null;
        related_post_id?: string | null;
        related_comment_id?: string | null;
        related_collaboration_id?: string | null;
        related_event_id?: string | null;
        metadata?: Record<string, unknown>;
      };
    };
  };
}
